import { Container } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { Breakpoint } from '@mui/system/createTheme';
import React from 'react';

import BackgroundIcon from '@components/BackgroundIcon';
import HeaderArc from '@components/DrilldownHeader/HeaderArc';
import LocationBreadcrumb from '@components/LocationBreadcrumb';
import MainTitle from '@components/MainTitle';
import { Spacer } from '@components/MainTitle/MainTitle.style';

import { useFontSize } from '@hooks/useFontSize';
import { useIsMobile } from '@hooks/useIsMobile';

import AppLayout from '@layouts/AppLayout';
import { BigTitle, BigTitleWrapper } from '@layouts/Layouts/BigTitleLayout';

import type { ImageType } from '@models/image-type';

const MainWrapper: React.FC<{
  friendlyName?: string;
  loading?: boolean;
  title: string;
  backgroundIcon?: ImageType;
  children: React.ReactNode;
  mobileWidth?: Breakpoint;
  style?: React.CSSProperties;
  showHeader?: boolean;
  showBreadcrumb?: boolean;
  maxContentWidth?: Breakpoint | false;
  /** Explicitly hide the main title */
  hideTitle?: boolean;
}> = ({
  friendlyName,
  loading,
  title,
  backgroundIcon,
  children,
  mobileWidth = 'sm',
  style,
  showHeader,
  showBreadcrumb = true,
  maxContentWidth,
  hideTitle,
}) => {
  const mobile = useIsMobile(mobileWidth);
  const titleFontSize = useFontSize(72);
  const { palette } = useTheme();

  return (
    <AppLayout
      friendlyName={friendlyName}
      loading={loading}
      showHeader={showHeader}
    >
      {!hideTitle && (
        <>
          {showHeader && mobile ? (
            <>
              {showBreadcrumb ? (
                <LocationBreadcrumb color={palette.grey.A200} />
              ) : null}
              <MainTitle title={title} />
              <Spacer />
            </>
          ) : (
            <>
              <BigTitleWrapper>
                {showBreadcrumb && mobile ? (
                  <LocationBreadcrumb color={palette.grey.A200} />
                ) : null}
                <BigTitle fontSize={titleFontSize}>{title}</BigTitle>
              </BigTitleWrapper>
              <HeaderArc />
            </>
          )}
        </>
      )}
      <Container maxWidth={maxContentWidth} sx={{ ...style, paddingTop: 2 }}>
        <BackgroundIcon image={backgroundIcon} />
        {children}
      </Container>
    </AppLayout>
  );
};

export default MainWrapper;
